/**
 * LtiConfigPage — Phase 2.0
 * Developer-only admin page for Canvas LTI 1.3 integration.
 *
 *   Top card:   the 3 URLs admins paste into their Canvas Developer Key.
 *   Mid card:   form to register a new Canvas tenant (issuer + client_id + deployment).
 *   Bottom:     list of registered tenants with delete.
 *
 * Once tenants are registered, the OIDC login flow at /api/lti/login will
 * accept incoming Canvas launches and 302 the user into /lti/launch-landing.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import {
  getLtiConfig, listLtiTenants, createLtiTenant, deleteLtiTenant,
} from '../lib/apiClient';

const CopyableRow = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* ignore */ }
  };
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0" data-testid={testid}>
      <div className="w-56 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500 pt-1">{label}</div>
      <div className="flex-1 font-mono text-sm break-all text-gray-900 bg-gray-50 rounded px-2 py-1 border border-gray-200">{value}</div>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 text-xs px-2.5 py-1 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
        data-testid={`${testid}-copy`}
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
    </div>
  );
};

export default function LtiConfigPage() {
  const [config, setConfig] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    tenant_id: '', name: '',
    canvas_issuer: 'https://canvas.instructure.com',
    client_id: '', deployment_ids: '',
  });

  const refresh = async () => {
    try {
      const [c, t] = await Promise.all([getLtiConfig(), listLtiTenants()]);
      setConfig(c);
      setTenants(t.tenants || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    }
  };

  useEffect(() => { refresh(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const body = {
        tenant_id: form.tenant_id.trim(),
        name: form.name.trim(),
        canvas_issuer: form.canvas_issuer.trim().replace(/\/$/, ''),
        client_id: form.client_id.trim(),
        deployment_ids: form.deployment_ids
          .split(',').map((s) => s.trim()).filter(Boolean),
      };
      await createLtiTenant(body);
      setForm({
        tenant_id: '', name: '',
        canvas_issuer: 'https://canvas.instructure.com',
        client_id: '', deployment_ids: '',
      });
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (tid) => {
    if (!window.confirm(`Remove Canvas tenant "${tid}"?`)) return;
    try {
      await deleteLtiTenant(tid);
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="lti-config-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Phase 2.0</p>
            <h1 className="text-2xl font-bold text-gray-900">Canvas LTI 1.3 Integration</h1>
            <p className="text-sm text-gray-600 mt-1">Embed CourseForge inside any Canvas course as a native tab.</p>
          </div>
          <Link to="/developer" className="text-sm text-emerald-700 hover:underline">← Back to console</Link>
        </div>

        {err && (
          <div className="mb-4 p-3 rounded border border-red-300 bg-red-50 text-red-800 text-sm" data-testid="lti-error">
            {err}
          </div>
        )}

        {/* CANVAS REGISTRATION URLs */}
        <section className="card p-5 mb-6" data-testid="lti-config-urls">
          <h2 className="text-base font-bold text-gray-900 mb-1">
            Step 1 — Hand these URLs to the Canvas admin
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            They go into Canvas → Admin → Developer Keys → <em>+ LTI Key</em>.
            Set Method to <strong>Manual Entry</strong>.
          </p>
          {config ? (
            <div>
              <CopyableRow
                label="OpenID Connect Initiation URL"
                value={config.openid_initiation_url}
                testid="row-openid-init"
              />
              <CopyableRow
                label="Target Link URI / Redirect URI"
                value={config.target_link_uri}
                testid="row-target-link"
              />
              <CopyableRow
                label="Public JWK URL"
                value={config.public_jwk_url}
                testid="row-jwks"
              />
              <CopyableRow
                label="Tool kid (informational)"
                value={config.tool_kid}
                testid="row-kid"
              />
              <div className="mt-4 text-xs text-gray-600 leading-relaxed">
                <p className="font-semibold text-gray-700 mb-1">Recommended Developer Key settings:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Privacy Level: <strong>Public</strong></li>
                  <li>Placement: <strong>Course Navigation</strong></li>
                  <li>Enable LTI Advantage Services (AGS lineitem/score, NRPS).</li>
                </ul>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Loading…</p>
          )}
        </section>

        {/* REGISTER TENANT */}
        <section className="card p-5 mb-6" data-testid="lti-tenant-form">
          <h2 className="text-base font-bold text-gray-900 mb-1">
            Step 2 — Register the Canvas tenant
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            After Canvas issues a <strong>Client ID</strong> and <strong>Deployment ID</strong>,
            register them here so launches are recognized.
          </p>
          <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              required
              placeholder="tenant_id (slug, e.g. unt-health)"
              value={form.tenant_id}
              onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
              data-testid="input-tenant-id"
            />
            <input
              required
              placeholder="Institution Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
              data-testid="input-tenant-name"
            />
            <input
              required
              placeholder="Canvas Issuer (e.g. https://canvas.instructure.com)"
              value={form.canvas_issuer}
              onChange={(e) => setForm({ ...form, canvas_issuer: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 text-sm md:col-span-2"
              data-testid="input-canvas-issuer"
            />
            <input
              required
              placeholder="Canvas Client ID (Developer Key ID)"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
              data-testid="input-client-id"
            />
            <input
              placeholder="Deployment IDs (comma-separated)"
              value={form.deployment_ids}
              onChange={(e) => setForm({ ...form, deployment_ids: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
              data-testid="input-deployment-ids"
            />
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                data-testid="btn-create-tenant"
              >
                {busy ? 'Registering…' : 'Register Canvas Tenant'}
              </button>
            </div>
          </form>
        </section>

        {/* TENANT LIST */}
        <section className="card p-5" data-testid="lti-tenant-list">
          <h2 className="text-base font-bold text-gray-900 mb-3">
            Registered Canvas Tenants ({tenants.length})
          </h2>
          {tenants.length === 0 ? (
            <p className="text-sm text-gray-500">No tenants registered yet.</p>
          ) : (
            <div className="space-y-3">
              {tenants.map((t) => (
                <div
                  key={t.tenant_id}
                  className="border border-gray-200 rounded p-4 flex items-start justify-between gap-3"
                  data-testid={`tenant-row-${t.tenant_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-500 mb-1">tenant_id: <code>{t.tenant_id}</code></p>
                    <p className="font-mono text-xs text-gray-700 break-all">
                      iss: {t.canvas_issuer}
                    </p>
                    <p className="font-mono text-xs text-gray-700 break-all">
                      client_id: {t.client_id}
                    </p>
                    {(t.deployment_ids || []).length > 0 && (
                      <p className="font-mono text-xs text-gray-700 break-all">
                        deployments: {t.deployment_ids.join(', ')}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => onDelete(t.tenant_id)}
                    className="shrink-0 text-xs px-3 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                    data-testid={`btn-delete-${t.tenant_id}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
